import { buildModuleCacheKey } from 'lib/react-query-utils'

import { GetChargesInput, GetPlanDetailsInput, GetPlansInput } from './plan'
import {
  CreateGitlabIntegrationInput,
  GetGitHubRepositoriesInput,
  GetGitHubRepositoryBranchesInput,
  GetGitlabIntegrationLinkInput,
  GetGitLabRepositoriesInput,
  GetGitLabRepositoryBranchesInput,
  GetProjectDetailsInput,
  GetProjectFigmaDetailsProps,
  GetProjectGitBranchesInput,
  GetProjectHistoryInput,
  GetProjectHistorySubeventsInput,
  GetProjectLanguageActivityListInput,
  GetProjectReposInput,
  GetProjectsInput,
  GetProjectTagsInput,
  GetProjectUsersInput,
} from './projects'
import { GetSocialProviderLinkInput } from './social'
import {
  GetTermDetailsInput,
  GetTermDuplicatesInput,
  GetTermKeysDirectoriesListInput,
  GetTermsColumnsInput,
  GetTermsListInput,
} from './terms'

/**
 * Same list can be displayed with the standard page pagination in one place (useQuery)
 * and with the infinite scroll pagination in another place (useInfiniteQuery)
 * We want to keep appropriate query cache entries for them separately
 * So we add { infinite: true } to the query key used with useInfiniteQuery
 */
export type InfiniteQueryKey = {
  infinite?: true
}

/**
 * `qk` stands for "query keys"
 */
export const qk = buildModuleCacheKey({
  auth: {
    token: {
      refresh: null,
    },
  },
  charges: {
    list: (input: GetChargesInput) => [input],
  },
  users: {
    myProfile: null,
  },
  github: {
    repositories: (input: GetGitHubRepositoriesInput & InfiniteQueryKey) => [
      input,
    ],
    branches: (input: GetGitHubRepositoryBranchesInput & InfiniteQueryKey) => [
      input,
    ],
  },
  gitlab: {
    createIntegration: (input: CreateGitlabIntegrationInput) => [input],
    repositories: (input: GetGitLabRepositoriesInput & InfiniteQueryKey) => [
      input,
    ],
    branches: (input: GetGitLabRepositoryBranchesInput & InfiniteQueryKey) => [
      input,
    ],
  },
  plans: {
    list: (input: GetPlansInput & InfiniteQueryKey) => [input],
    details: (input: GetPlanDetailsInput) => [input],
  },
  projects: {
    list: (input: GetProjectsInput & InfiniteQueryKey) => [input],
    details: (input: GetProjectDetailsInput) => [input],
    history: (input: GetProjectHistoryInput & InfiniteQueryKey) => [input],
    languageActivityList: (input: GetProjectLanguageActivityListInput) => [
      input,
    ],
    historySubEvents: (input: GetProjectHistorySubeventsInput) => [input],
    tags: (input: GetProjectTagsInput) => [input],
    users: (input: GetProjectUsersInput) => [input],
    reposList: (input: GetProjectReposInput) => [input],
    gitlabLink: (input: GetGitlabIntegrationLinkInput) => [input],
    branchesList: (input: GetProjectGitBranchesInput & InfiniteQueryKey) => [
      input,
    ],
    updates: (input: GetProjectUsersInput) => [input],
    figmaFile: (input: GetProjectFigmaDetailsProps) => [input],
  },
  terms: {
    details: (input: GetTermDetailsInput) => [input],
    list: (input: GetTermsListInput & InfiniteQueryKey) => [input],
    columns: (input: GetTermsColumnsInput) => [input],
    duplicates: (input: GetTermDuplicatesInput) => [input],
    keysDirectoriesList: (input: GetTermKeysDirectoriesListInput) => [input],
  },
  social: {
    providerLink: (input: GetSocialProviderLinkInput) => [input],
  },
  languages: {
    list: null,
    appLanguages: null,
  },
  common: {
    appVersion: null,
  },
  userSettings: {
    apiKey: null,
    cliReleaseDate: null,
    settings: null,
  },
})
